import { NgxPermissionsModule } from 'ngx-permissions';
import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRippleModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  DragDropModule,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';

import { fuseAnimations } from '../../../../@fuse/animations';
import { FuseTranslationLoaderService } from '../../../../@fuse/services/translation-loader.service';
import { ProductsService } from '../../api/products.service';
import { VenteIncitativeV3Service } from '../../api/vente-incitative-v3.service';
import {
  UpsellCheckoutTriggerV3,
  UpsellForProductPayloadV3,
  UpsellV3,
} from '../../api/vente-incitative-v3.model';
import { GT2PageAbstract } from '../../content/abstract/GT2PageAbstract';
import { MobileLayoutService } from '../../services/mobile-layout.service';
import { ProductUpsellPanelService } from '../../services/product-upsell-panel.service';
import { locale as english } from './i18n/en-CA';
import { locale as french } from './i18n/fr-CA';

interface SuggestionItem {
  /** uuid de la suggestion existante (préservé pour le full-replace). */
  uuid?: string;
  product_uuid: string;
  name: string;
  /** Prix en cents (absent pour un produit fraîchement cherché sans montant). */
  amount?: number | null;
}

/**
 * Side panel « Suggestions (upsell) » de la fiche produit — flux 1-à-N :
 * gauche = recherche catalogue, droite = suggestions ordonnées de CE produit
 * dans CETTE boutique. Rien n'est persisté avant « Enregistrer » (PUT upsert
 * full-replace). Si l'upsell est partagé (groupe hérité multi-déclencheurs),
 * un avertissement s'affiche : la sauvegarde s'applique à tous les produits
 * du groupe.
 */
@Component({
  selector: 'app-product-upsell-panel',
  templateUrl: './product-upsell-panel.component.html',
  styleUrls: ['./product-upsell-panel.component.scss'],
  animations: fuseAnimations,
  standalone: true,
  imports: [
    NgxPermissionsModule,
    CommonModule,
    FormsModule,
    DragDropModule,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRippleModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltipModule,
  ],
})
export class ProductUpsellPanelComponent
  extends GT2PageAbstract
  implements OnInit
{
  shopUuid!: string;
  shopName: string | null = null;
  productUuid!: string;
  productName: string | null = null;

  upsell: UpsellV3 | null = null;
  suggestions: SuggestionItem[] = [];
  searchResults: any[] = [];
  filterValue: string = '';
  titleFr: string = '';
  titleEn: string = '';
  isActive: boolean = true;
  /** Ouverture aussi au passage à la caisse ; null = jamais. */
  checkoutTrigger: UpsellCheckoutTriggerV3 | null = null;
  readonly checkoutTriggerOptions: {
    value: UpsellCheckoutTriggerV3 | null;
    label: string;
  }[] = [
    { value: null, label: 'PRODUCT_UPSELL_PANEL.CHECKOUT_TRIGGER_NONE' },
    {
      value: 'TRIGGER_PRODUCT',
      label: 'PRODUCT_UPSELL_PANEL.CHECKOUT_TRIGGER_TRIGGER_PRODUCT',
    },
    {
      value: 'ALCOHOLIC_PRODUCT',
      label: 'PRODUCT_UPSELL_PANEL.CHECKOUT_TRIGGER_ALCOHOLIC_PRODUCT',
    },
    { value: 'ALWAYS', label: 'PRODUCT_UPSELL_PANEL.CHECKOUT_TRIGGER_ALWAYS' },
  ];
  showAdvanced: boolean = false;

  isLoading: boolean = false;
  isSearching: boolean = false;
  isSaving: boolean = false;
  readonly minSearchChars = 2;
  readonly mobileLayout = inject(MobileLayoutService);

  constructor(
    private panelService: ProductUpsellPanelService,
    private translationLoader: FuseTranslationLoaderService,
    public translate: TranslateService,
    private viService: VenteIncitativeV3Service,
    private productsService: ProductsService,
    private dialog: MatDialog,
    public router: Router,
    private logger: NGXLogger,
  ) {
    super();
    this.translationLoader.loadTranslations(english, french);
    const data = this.panelService.getData();
    if (data) {
      this.shopUuid = data.shopUuid;
      this.shopName = data.shopName ?? null;
      this.productUuid = data.productUuid;
      this.productName = data.productName ?? null;
    }
  }

  ngOnInit(): void {
    this.loadUpsell();
  }

  /** Nombre d'autres produits déclencheurs partageant cet upsell (groupe hérité). */
  get sharedCount(): number {
    if (!this.upsell) {
      return 0;
    }
    return Math.max(0, (this.upsell.product_uuids || []).length - 1);
  }

  get canSearch(): boolean {
    return this.filterValue.trim().length >= this.minSearchChars;
  }

  /** Résultats de recherche sans les produits déjà suggérés ni le produit courant. */
  get availableResults(): any[] {
    const taken = new Set(this.suggestions.map((s) => s.product_uuid));
    taken.add(this.productUuid);
    return this.searchResults.filter((p) => !taken.has(p.uuid));
  }

  public loadUpsell(): void {
    this.isLoading = true;
    this.viService
      .getUpsellForProduct(this.shopUuid, this.productUuid)
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.applyUpsell(response.data);
        },
        error: (error: any) => {
          this.logger.error(
            'ProductUpsellPanelComponent.loadUpsell() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.isLoading = false;
          this.handleAPIError(error, this.dialog, null, null, this.router);
        },
      });
  }

  private applyUpsell(upsell: UpsellV3 | null): void {
    this.upsell = upsell;
    if (upsell === null) {
      this.suggestions = [];
      this.titleFr = '';
      this.titleEn = '';
      this.isActive = true;
      this.checkoutTrigger = null;
      return;
    }
    this.titleFr = upsell.title_label_local?.title_label_fr_CA || '';
    this.titleEn = upsell.title_label_local?.title_label_en_CA || '';
    this.isActive = upsell.is_active;
    this.checkoutTrigger = upsell.checkout_trigger ?? null;
    if (this.checkoutTrigger !== null) {
      this.showAdvanced = true;
    }
    this.suggestions = (upsell.suggestions || []).map((s) => ({
      uuid: s.uuid,
      product_uuid: s.product_uuid,
      name:
        this.translate.currentLang === 'en-CA'
          ? s.name_local?.name_en_CA || s.name_local?.name_fr_CA || ''
          : s.name_local?.name_fr_CA || s.name_local?.name_en_CA || '',
      amount: s.amount,
    }));
  }

  public onSearchChange(): void {
    if (!this.canSearch) {
      this.searchResults = [];
      return;
    }
    this.isSearching = true;
    this.productsService.getProducts(this.filterValue, null, []).subscribe({
      next: (response: any) => {
        this.isSearching = false;
        this.searchResults = response.data || [];
      },
      error: (error: any) => {
        this.logger.error(
          'ProductUpsellPanelComponent.onSearchChange() -> ERROR: ' +
            JSON.stringify(error),
        );
        this.isSearching = false;
        this.searchResults = [];
      },
    });
  }

  public clearSearch(): void {
    this.filterValue = '';
    this.searchResults = [];
  }

  public onAddItem(product: any): void {
    this.suggestions.push({
      product_uuid: product.uuid,
      name: product.name_internal || product.name,
      amount: null,
    });
  }

  public onRemoveItem(index: number): void {
    this.suggestions.splice(index, 1);
  }

  /** Drop dans la liste de droite : même liste = reorder local, sinon ajout. */
  public onSuggestionsDrop(event: any): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        this.suggestions,
        event.previousIndex,
        event.currentIndex,
      );
      return;
    }
    const product = event.item.data;
    const taken = this.suggestions.some((s) => s.product_uuid === product.uuid);
    if (taken || product.uuid === this.productUuid) {
      return;
    }
    transferArrayItem(
      [product],
      this.suggestions as any[],
      0,
      event.currentIndex,
    );
    this.suggestions[event.currentIndex] = {
      product_uuid: product.uuid,
      name: product.name_internal || product.name,
      amount: null,
    };
  }

  public formatAmount(cents: number | null | undefined): string {
    if (cents === null || cents === undefined) {
      return '';
    }
    return (cents / 100).toFixed(2) + ' $';
  }

  public onSave(): void {
    if (this.isSaving) {
      return;
    }
    const payload: UpsellForProductPayloadV3 = {
      is_active: this.isActive,
      checkout_trigger: this.checkoutTrigger,
      suggestions: this.suggestions.map((s, index) => ({
        uuid: s.uuid,
        product_uuid: s.product_uuid,
        position: index + 1,
      })),
    };
    // Titre : envoyé seulement si l'usager a saisi quelque chose — sinon le
    // serveur applique/conserve le défaut (« Vous aimeriez aussi…? »).
    if (this.titleFr.trim() !== '' || this.titleEn.trim() !== '') {
      payload.title_label = {
        fr_CA: this.titleFr.trim() || undefined,
        en_CA: this.titleEn.trim() || undefined,
      };
    }

    this.isSaving = true;
    this.viService
      .upsertUpsellForProduct(this.shopUuid, this.productUuid, payload)
      .subscribe({
        next: (response) => {
          this.isSaving = false;
          this.panelService.emitResult(response);
        },
        error: (error: any) => {
          this.logger.error(
            'ProductUpsellPanelComponent.onSave() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.isSaving = false;
          this.handleAPIError(error, this.dialog, null, null, this.router);
        },
      });
  }

  public close(): void {
    this.panelService.emitResult(false);
  }
}
